import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Camera, Loader2, Sparkles, Download, RotateCcw, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { resolveImageUrl } from '../../../lib/imageCache';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Shopper uploads a photo of their own room; we return the same photo with the chosen fabric on the windows.
export const RoomRenderDialog = ({ open, onOpenChange, entry, onRendered }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [quota, setQuota] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API_URL}/room-render/quota`).then(({ data }) => setQuota(data)).catch(() => setQuota(null));
  }, [open, result]);

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!/^image\/(jpeg|png|webp|heic|heif)$/.test(f.type)) return toast.error('Please choose a JPEG, PNG or WebP photo');
    if (f.size > 12 * 1024 * 1024) return toast.error('Photo is larger than 12MB');
    setFile(f);
    setResult(null);
    setPreview(URL.createObjectURL(f));
  };

  const render = async () => {
    if (!file || !entry) return;
    setRendering(true);
    try {
      const form = new FormData();
      form.append('color_id', entry.id);
      form.append('photo', file);
      const { data } = await axios.post(`${API_URL}/room-render`, form, { timeout: 120000 });
      setResult(data);
      onRendered?.({ ...data, color_id: entry.id });
      toast.success(`Here's ${entry.name} in your room`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rendering failed. Please try another photo.');
    } finally {
      setRendering(false);
    }
  };

  const reset = () => { setFile(null); setPreview(null); setResult(null); };
  const close = (v) => { onOpenChange(v); if (!v) reset(); };
  const exhausted = quota && quota.remaining === 0;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl" data-testid="room-render-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2"><Camera className="w-5 h-5 text-amber-600" /> See {entry?.name} in your room</DialogTitle>
          <DialogDescription>Upload a photo of your room with the windows visible. We'll dress them in this fabric — everything else stays exactly as it is.</DialogDescription>
        </DialogHeader>

        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="room-render-input" />

        {!preview ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={exhausted}
            data-testid="room-render-dropzone"
            className="w-full aspect-[16/9] rounded-xl border-2 border-dashed border-neutral-300 hover:border-amber-400 bg-neutral-50 flex flex-col items-center justify-center gap-2 text-neutral-500 disabled:opacity-60"
          >
            <Upload className="w-8 h-8" />
            <span className="font-medium text-neutral-700">Choose a room photo</span>
            <span className="text-xs">JPEG, PNG or WebP · up to 12MB · best with daylight and the full window in frame</span>
          </button>
        ) : (
          <div className={`grid gap-4 ${result ? 'md:grid-cols-2' : ''}`}>
            <div className="space-y-1">
              <p className="text-[11px] uppercase tracking-wider text-neutral-400">Your photo</p>
              <img src={preview} alt="Your room" className="w-full rounded-xl object-contain bg-neutral-100 max-h-[420px]" data-testid="room-render-preview" />
            </div>
            {result && (
              <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-wider text-amber-700 flex items-center gap-1"><Sparkles className="w-3 h-3" /> With {result.color_name}</p>
                <img src={resolveImageUrl(result.url)} alt={`Your room with ${result.color_name}`} className="w-full rounded-xl object-contain bg-neutral-100 max-h-[420px]" data-testid="room-render-result" />
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <p className="text-xs text-neutral-500" data-testid="room-render-quota">
            {quota ? (exhausted ? `You've used your ${quota.limit} free renders this hour — try again shortly.` : `${quota.remaining} of ${quota.limit} free renders left this hour`) : ''}
          </p>
          <div className="flex gap-2">
            {preview && (
              <Button variant="outline" onClick={reset} disabled={rendering} className="gap-1.5" data-testid="room-render-reset"><RotateCcw className="w-4 h-4" /> Another photo</Button>
            )}
            {result ? (
              <Button asChild className="gap-1.5 bg-neutral-900 hover:bg-neutral-800">
                <a href={resolveImageUrl(result.url)} download={`apollo-shades-${entry?.name}.png`} target="_blank" rel="noopener noreferrer" data-testid="room-render-download"><Download className="w-4 h-4" /> Download</a>
              </Button>
            ) : (
              <Button onClick={render} disabled={!file || rendering || exhausted} className="gap-1.5 bg-amber-600 hover:bg-amber-700" data-testid="room-render-submit">
                {rendering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {rendering ? 'Rendering (~20s)…' : 'Render my room'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RoomRenderDialog;
